import axiosClient from "../api/axiosClient";

/**
 * Account & Business Settings service.
 * Ported 1:1 from the React Native app's profile-update, add-business-detail,
 * change-password, email-update, and phone-number-update screens
 * (src/Screens/*) and the API functions they call in
 * src/services/api/index.js + src/services/constants/index.js.
 * No new backend endpoints — all of the below are already live.
 */

// --- Profile ---
export const getMyProfile = (config) => axiosClient.get("/Account/Profile", config);

/**
 * @param {object} payload - plain object; sent as a JSON string body, matching
 * mobile's onUpdateProfile. Omit PhoneNumber/Email keys entirely when unchanged
 * (mobile deletes them from the payload rather than sending the old value).
 */
export const updateProfile = (payload) =>
  axiosClient.post("/Account/UpdateProfile", JSON.stringify(payload));

// --- Business details (Promoter accounts only) ---
export const addBusinessInfo = (payload) =>
  axiosClient.post("/Account/AddBusinessDetails", JSON.stringify(payload));

// --- Password ---
export const changePassword = (payload) =>
  axiosClient.post("/Account/ChangePassword", JSON.stringify(payload));

/** @param {object} payload - {Token, RefreshToken, ExpiryDate, Issuer} */
export const refreshAuthToken = (payload) =>
  axiosClient.post("/Account/RefreshToken", JSON.stringify(payload));

// --- OTP (phone/email verification) ---
// SendTo is required by the backend to route Email vs Phone (it defaults to
// "Phone" when omitted, which happened to work here since phoneNumber is
// always real, but was an implicit reliance on that default rather than an
// explicit choice — see resendForgotPasswordOtp in authService.js for the
// case where omitting it was an actual live bug).
export const resendProfileOtp = (email, phoneNumber) =>
  axiosClient.post("/Account/SendPhoneOtp", JSON.stringify({ Email: email, PhoneNumber: phoneNumber, SendTo: "Phone" }));

export const verifyProfileOtp = (otp, userId) =>
  axiosClient.post(`/Account/VerifyOTP/${otp}/${userId}`);

// --- Uploads ---
/**
 * Single-file upload for avatar / business logo.
 * @param {File} file
 * @returns {Promise<string|null>} the uploaded URL, or null on failure
 */
export const uploadSingleImage = async (file) => {
  const formData = new FormData();
  formData.append("Image", file);
  const res = await axiosClient.post("/Image/upload", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 300000,
  });
  const body = res.data;
  const ok = body?.status === "Success" || body?.Status === "Success";
  return ok ? body?.result ?? body?.Result ?? null : null;
};

/**
 * Business document upload (company registration doc / owner ID doc).
 * Same `Image/Bulk` endpoint already used for GIF upload in createEventService.js,
 * generalized here for a single document file.
 * @param {File} file
 * @param {string} folderName - "CompanyDocument" | "OwnerDocument"
 * @returns {Promise<string|null>} the uploaded URL, or null on failure
 */
export const uploadBusinessDocument = async (file, folderName) => {
  const formData = new FormData();
  formData.append("files", file);
  formData.append("folderName", folderName);
  const res = await axiosClient.post("/Image/Bulk", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 300000,
  });
  const body = res.data;
  const ok = body?.status === "Success" || body?.Status === "Success";
  const result = body?.result ?? body?.Result;
  const first = Array.isArray(result) ? result[0] : result;
  return ok ? first?.originalUrl ?? first?.OriginalUrl ?? first?.url ?? first?.Url ?? null : null;
};
